import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface EditBookDatesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  book: {
    id: string;
    title: string;
    author: string;
  };
  initialData?: {
    startedAt?: string;
    finishedAt?: string;
  };
  onSubmit: (data: {
    startedAt?: Date;
    finishedAt?: Date;
  }) => Promise<void>;
}

export function EditBookDatesDialog({ 
  isOpen, 
  onClose, 
  book, 
  initialData,
  onSubmit 
}: EditBookDatesDialogProps) {
  const [startedAt, setStartedAt] = useState<Date | undefined>(
    initialData?.startedAt ? new Date(initialData.startedAt) : undefined
  );
  const [finishedAt, setFinishedAt] = useState<Date | undefined>(
    initialData?.finishedAt ? new Date(initialData.finishedAt) : new Date()
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Controlled month state for year/month dropdown navigation
  const [startedMonth, setStartedMonth] = useState<Date>(
    initialData?.startedAt ? new Date(initialData.startedAt) : new Date()
  );
  const [finishedMonth, setFinishedMonth] = useState<Date>(
    initialData?.finishedAt ? new Date(initialData.finishedAt) : new Date()
  );

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (isOpen) {
      setStartedAt(initialData?.startedAt ? new Date(initialData.startedAt) : undefined);
      setFinishedAt(initialData?.finishedAt ? new Date(initialData.finishedAt) : undefined);
      setStartedMonth(initialData?.startedAt ? new Date(initialData.startedAt) : new Date());
      setFinishedMonth(initialData?.finishedAt ? new Date(initialData.finishedAt) : new Date());
    }
  }, [isOpen, initialData]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit({
        startedAt,
        finishedAt,
      });
      onClose();
    } catch (error) {
      console.error('Error updating dates:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Reading Dates</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{book.title}</span>
            <br />
            <span className="text-sm text-muted-foreground">by {book.author}</span>
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Date Started */}
          <div className="space-y-2">
            <Label>Date Started</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !startedAt && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startedAt ? format(startedAt, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startedAt}
                  onSelect={setStartedAt}
                  month={startedMonth}
                  onMonthChange={setStartedMonth}
                  initialFocus
                  disabled={(date) => date > new Date()}
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Date Finished */}
          <div className="space-y-2">
            <Label>Date Finished</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !finishedAt && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {finishedAt ? format(finishedAt, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={finishedAt}
                  onSelect={setFinishedAt}
                  month={finishedMonth}
                  onMonthChange={setFinishedMonth}
                  initialFocus
                  disabled={(date) => {
                    if (date > new Date()) return true;
                    if (startedAt && date < startedAt) return true;
                    return false;
                  }}
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Dates'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
